import { CARD_STATE_COLORS } from "../../../config/chartColors";
import type { LessonProgressDetail } from "../../../generated/graphql";

interface LessonProgressBarsProps {
  lessons: LessonProgressDetail[];
}

const STATE_COLORS = CARD_STATE_COLORS;

export function LessonProgressBars({ lessons }: LessonProgressBarsProps) {
  if (!lessons || lessons.length === 0) {
    return (
      <div className="chart-container">
        <div className="chart-empty">No lesson progress data available</div>
      </div>
    );
  }

  return (
    <div className="chart-container">
      <div className="chart-header">
        <h4 className="h6">Lesson Progress</h4>
      </div>
      <div className="chart-body">
        <div className="space-y-6">
          {lessons.map((lesson) => (
            <div key={lesson.lessonId} className="space-y-2">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-medium text-secondary-200">{lesson.lessonName}</h4>
                <span className="text-xs text-gray-400">{lesson.totalCards} cards</span>
              </div>

              <div className="flex h-8 rounded-lg overflow-hidden">
                {lesson.newCards > 0 && (
                  <div
                    className="flex items-center justify-center text-xs font-medium text-white"
                    style={{
                      width: `${(lesson.newCards / lesson.totalCards) * 100}%`,
                      backgroundColor: STATE_COLORS.new,
                    }}
                    title={`New: ${lesson.newCards}`}
                  >
                    {lesson.newCards > 0 && `${lesson.newCards}`}
                  </div>
                )}
                {lesson.learningCards > 0 && (
                  <div
                    className="flex items-center justify-center text-xs font-medium text-white"
                    style={{
                      width: `${(lesson.learningCards / lesson.totalCards) * 100}%`,
                      backgroundColor: STATE_COLORS.learning,
                    }}
                    title={`Learning: ${lesson.learningCards}`}
                  >
                    {lesson.learningCards > 0 && `${lesson.learningCards}`}
                  </div>
                )}
                {lesson.reviewCards > 0 && (
                  <div
                    className="flex items-center justify-center text-xs font-medium text-white"
                    style={{
                      width: `${(lesson.reviewCards / lesson.totalCards) * 100}%`,
                      backgroundColor: STATE_COLORS.review,
                    }}
                    title={`Review: ${lesson.reviewCards}`}
                  >
                    {lesson.reviewCards > 0 && `${lesson.reviewCards}`}
                  </div>
                )}
                {lesson.relearnCards > 0 && (
                  <div
                    className="flex items-center justify-center text-xs font-medium text-white"
                    style={{
                      width: `${(lesson.relearnCards / lesson.totalCards) * 100}%`,
                      backgroundColor: STATE_COLORS.relearning,
                    }}
                    title={`Relearning: ${lesson.relearnCards}`}
                  >
                    {lesson.relearnCards > 0 && `${lesson.relearnCards}`}
                  </div>
                )}
              </div>

              <div className="flex items-center justify-between text-xs">
                <div className="flex gap-4">
                  <div className="flex items-center gap-1">
                    <div
                      className="w-3 h-3 rounded"
                      style={{ backgroundColor: STATE_COLORS.new }}
                    />
                    <span className="text-gray-400">New</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div
                      className="w-3 h-3 rounded"
                      style={{ backgroundColor: STATE_COLORS.learning }}
                    />
                    <span className="text-gray-400">Learning</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div
                      className="w-3 h-3 rounded"
                      style={{ backgroundColor: STATE_COLORS.review }}
                    />
                    <span className="text-gray-400">Review</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div
                      className="w-3 h-3 rounded"
                      style={{ backgroundColor: STATE_COLORS.relearning }}
                    />
                    <span className="text-gray-400">Relearning</span>
                  </div>
                </div>
                <span className="font-medium text-secondary-200">
                  {lesson.completionPercentage.toFixed(0)}% complete
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
